import { Injectable, inject, signal, computed } from '@angular/core';
import { NotificationApiService } from '../api/notification-api.service';
import { NotificationHubService } from './notification-hub.service';
import { Notification } from '../models/notification.models';

@Injectable({ providedIn: 'root' })
export class NotificationStateService {
  private readonly api = inject(NotificationApiService);
  private readonly hub = inject(NotificationHubService);

  private initialized = false;

  readonly notifications = signal<Notification[]>([]);
  readonly unreadCount = computed(() => this.notifications().filter((n) => !n.isRead).length);

  /** Loads the current notification list and opens the live SignalR connection. Idempotent. */
  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.refresh();
    this.hub.connect({
      onNotification: (notification) => this.prepend(notification),
      onNotificationUpdated: (notification) => this.updateInPlace(notification),
      onNotificationDeleted: (payload) => this.removeById(payload.notificationId),
      onReconnected: () => this.refresh(),
    });
  }

  markAsRead(id: number): void {
    this.api.markAsRead(id).subscribe({
      next: () => {
        this.notifications.update((list) =>
          list.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
        );
      },
    });
  }

  markAllRead(): void {
    this.api.markAllRead().subscribe({
      next: () => {
        this.notifications.update((list) => list.map((n) => ({ ...n, isRead: true })));
      },
    });
  }

  deleteNotification(id: number): void {
    this.api.deleteNotification(id).subscribe({
      next: () => this.removeById(id),
    });
  }

  private refresh(): void {
    this.api.getNotifications().subscribe({
      next: (data) => this.notifications.set(data),
    });
  }

  /** Guards against duplicates when a push and a refresh race each other. */
  private prepend(notification: Notification): void {
    this.notifications.update((list) =>
      list.some((n) => n.id === notification.id) ? list : [notification, ...list],
    );
  }

  private updateInPlace(notification: Notification): void {
    this.notifications.update((list) =>
      list.map((n) => (n.id === notification.id ? notification : n)),
    );
  }

  private removeById(id: number): void {
    this.notifications.update((list) => list.filter((n) => n.id !== id));
  }
}
